// Implements: F-SHR-003, F-SHR-005, F-SHR-006, F-SHR-008, F-SHR-009
/**
 * The reads and the three writes behind the share control and the links panel.
 *
 * Every read is keyed by mockup id (checklist 14). The mockup itself is the
 * SHARED `mockupDetailKey` the rest of the page reads, so deciding whether the
 * control shows costs no request of its own.
 *
 * **The URL never enters the query cache.** Create is a plain call, not a
 * `useMutation` (whose cache keeps the last answer for as long as the client
 * lives): its answer goes back to the caller, which holds it in the dialog's
 * state and drops it when the dialog closes; the list is refetched, and the
 * list carries no URL. Revoke refetches the list, whose state the server
 * derives, so the row changes without a reload.
 *
 * **Nor does a password.** Edit is a plain call too, for the same reason: a
 * `useMutation` would keep its variables, the password among them. Every write
 * invalidates the whole `["sharing"]` prefix, so the mockup's list and the
 * person's panel (`myLinksKey`) both show what the server now derives.
 */

import { useInfiniteQuery, useQuery, useQueryClient } from "@tanstack/react-query";
import type { InfiniteData, UseInfiniteQueryResult, UseQueryResult } from "@tanstack/react-query";
import { useCallback, useSyncExternalStore } from "react";

import type { ApiError } from "../../../shared/api/ApiError";
import { asApiError } from "../../../shared/api/asApiError";
import { fetchMockupDetail } from "../../../shared/api/mockupDetail";
import { mockupDetailKey, versionTreeKey } from "../../../shared/api/queryKeys";
import type { MockupDetailView, VersionTreeNode } from "../../../types/api";
import type {
  CreateShareLinkInput,
  EditShareLinkInput,
  MyLinksPageView,
  ShareLinkView,
} from "../../../types/sharing";
import {
  createShareLink,
  editShareLink,
  fetchMyLinks,
  fetchShareLinks,
  fetchShareableVersions,
  revokeShareLink,
} from "../api/sharingApi";

/** Every list of links lives under this prefix, so one write refreshes them all. */
export const SHARING_KEY = ["sharing"] as const;

export function shareLinksKey(mockupId: string): readonly ["sharing", "links", string] {
  return ["sharing", "links", mockupId];
}

/** The person's own links, across mockups (the panel). */
export const MY_LINKS_KEY = ["sharing", "mine"] as const;

export type UseMyLinksResult = UseInfiniteQueryResult<InfiniteData<MyLinksPageView>, ApiError>;

export function useMyLinks(): UseMyLinksResult {
  return useInfiniteQuery<
    MyLinksPageView,
    ApiError,
    InfiniteData<MyLinksPageView>,
    typeof MY_LINKS_KEY,
    string | undefined
  >({
    queryKey: MY_LINKS_KEY,
    queryFn: ({ pageParam }) => fetchMyLinks(pageParam),
    initialPageParam: undefined,
    getNextPageParam: (last) => last.next_cursor ?? undefined,
  });
}

/**
 * How many of the person's links are active, when the whole list is ALREADY in the cache (the sidebar
 * badge). The observer is disabled: it reads what the links page left behind and never asks the
 * server, so a person who has not opened that page costs no request, and a list that is only a first
 * page gives `null` (a partial count would read as the whole).
 */
export function useCachedActiveLinkCount(): number | null {
  const queryClient = useQueryClient();
  const cached = useInfiniteQuery<
    MyLinksPageView,
    ApiError,
    InfiniteData<MyLinksPageView>,
    typeof MY_LINKS_KEY,
    string | undefined
  >({
    queryKey: MY_LINKS_KEY,
    queryFn: ({ pageParam }) => fetchMyLinks(pageParam),
    initialPageParam: undefined,
    getNextPageParam: (last) => last.next_cursor ?? undefined,
    enabled: false,
  });
  // Read from the cache itself: the disabled observer is not told that a list was invalidated.
  const invalidated = useSyncExternalStore(
    (notify) => queryClient.getQueryCache().subscribe(notify),
    () => queryClient.getQueryState(MY_LINKS_KEY)?.isInvalidated === true,
  );
  // A write elsewhere (a link created or revoked) invalidated the list: the number would be a stale one.
  if (cached.data === undefined || cached.hasNextPage || invalidated) return null;
  return cached.data.pages.flatMap((page) => page.links).filter((link) => link.state === "active").length;
}

export function useShareTarget(mockupId: string): UseQueryResult<MockupDetailView, ApiError> {
  return useQuery<MockupDetailView, ApiError>({
    queryKey: mockupDetailKey(mockupId),
    queryFn: () => fetchMockupDetail(mockupId),
  });
}

export function useShareLinks(mockupId: string): UseQueryResult<readonly ShareLinkView[], ApiError> {
  return useQuery<readonly ShareLinkView[], ApiError>({
    queryKey: shareLinksKey(mockupId),
    queryFn: () => fetchShareLinks(mockupId),
  });
}

/**
 * The versions a frozen link may keep; read only once the person chooses a frozen link.
 *
 * The entry is the `versions` module's own (`versionTreeKey`), which the page's tree has usually
 * filled already: this observer takes it as it is (`staleTime: Infinity`; the picker is enabled
 * late, and enabling an observer refetches whatever it judges stale) instead of asking again. A new
 * version still reaches it, because whatever creates one invalidates that key, and invalidation
 * refetches every active observer whatever its `staleTime`.
 */
export function useShareableVersions(
  mockupId: string,
  enabled: boolean,
): UseQueryResult<readonly VersionTreeNode[], ApiError> {
  return useQuery<readonly VersionTreeNode[], ApiError>({
    queryKey: versionTreeKey(mockupId),
    queryFn: () => fetchShareableVersions(mockupId),
    enabled,
    staleTime: Number.POSITIVE_INFINITY,
  });
}

export type CreateOutcome =
  | { readonly ok: true; readonly id: string; readonly url: string; readonly hasPassword: boolean }
  | { readonly ok: false; readonly error: ApiError };

export function useCreateShareLink(): (
  mockupId: string,
  input: CreateShareLinkInput,
) => Promise<CreateOutcome> {
  const queryClient = useQueryClient();

  return useCallback(
    async (mockupId: string, input: CreateShareLinkInput): Promise<CreateOutcome> => {
      let id: string;
      let url: string;
      let hasPassword: boolean;
      try {
        ({ id, url, has_password: hasPassword } = await createShareLink(mockupId, input));
      } catch (cause) {
        return { ok: false, error: asApiError(cause) };
      }
      void queryClient.invalidateQueries({ queryKey: SHARING_KEY });
      return { ok: true, id, url, hasPassword };
    },
    [queryClient],
  );
}

export type RevokeOutcome = { readonly ok: true } | { readonly ok: false; readonly error: ApiError };

export function useRevokeShareLink(): (mockupId: string, linkId: string) => Promise<RevokeOutcome> {
  const queryClient = useQueryClient();

  return useCallback(
    async (mockupId: string, linkId: string): Promise<RevokeOutcome> => {
      try {
        await revokeShareLink(mockupId, linkId);
      } catch (cause) {
        return { ok: false, error: asApiError(cause) };
      }
      void queryClient.invalidateQueries({ queryKey: SHARING_KEY });
      return { ok: true };
    },
    [queryClient],
  );
}

export type EditOutcome = { readonly ok: true } | { readonly ok: false; readonly error: ApiError };

export function useEditShareLink(): (
  mockupId: string,
  linkId: string,
  input: EditShareLinkInput,
) => Promise<EditOutcome> {
  const queryClient = useQueryClient();

  return useCallback(
    async (mockupId: string, linkId: string, input: EditShareLinkInput): Promise<EditOutcome> => {
      try {
        await editShareLink(mockupId, linkId, input);
      } catch (cause) {
        const error = asApiError(cause);
        // A 409 says the link changed meanwhile (revoked): read the lists again so its row shows it.
        if (error.status === 409) void queryClient.invalidateQueries({ queryKey: SHARING_KEY });
        return { ok: false, error };
      }
      void queryClient.invalidateQueries({ queryKey: SHARING_KEY });
      return { ok: true };
    },
    [queryClient],
  );
}
